/**
 * AuthProvider — app-wide Firebase auth state.
 *
 * `onAuthStateChanged` is the single source of truth: it fires on sign-in,
 * sign-out, token refresh, and on page load when a persisted session is
 * restored. Whenever a Firebase user is present we sync it with the backend
 * to obtain the local profile (role + plan).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, type ProviderId } from '@/src/infrastructure/config/firebase';
import {
  describeAuthError,
  fetchProfile,
  logout as firebaseLogout,
  signInWithProvider,
  syncUser,
  type AuthResult,
  type SessionUser,
} from '@/src/modules/auth/session';
import { useAuthStore } from '@/src/modules/auth/useAuthStore';
import { RoleSelectionModal } from '@/src/modules/auth/RoleSelectionModal';
import { TermsAgreementModal } from '@/src/modules/auth/TermsAgreementModal';
import { trackEvent, setUserId } from '@/src/infrastructure/lib/analytics';

type AuthContextValue = {
  user: SessionUser | null;
  /** False until the first auth-state callback resolves (avoids UI flicker). */
  hydrated: boolean;
  loginWithGoogle: () => Promise<AuthResult>;
  loginWithFacebook: () => Promise<AuthResult>;
  loginWithMicrosoft: () => Promise<AuthResult>;
  logout: () => Promise<void>;
  /**
   * Replace the in-memory user. Call after a successful profile mutation so
   * every consumer of `useAuth().user` (sidebar avatar, header, etc.) reflects
   * the new values immediately without a page reload.
   */
  setUser: (next: SessionUser | null) => void;
  /** Re-fetch the canonical profile from `/v1/auth/me`. */
  refreshProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const { hydrated: rbacHydrated, roleSelectionRequired, termsAcceptanceRequired } =
    useAuthStore();

  const bypassEnabled = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

  const mockDevUser = useMemo<SessionUser>(
    () => ({
      id: 'dev-user',
      email: 'dev@local',
      displayName: 'Dev User',
      username: 'devuser',
      phone: null,
      avatar: null,
      provider: 'google',
      role: 'SUPER_ADMIN',
      plan: 'FREE',
      updatedAt: new Date().toISOString(),
    }),
    [],
  );

  useEffect(() => {
    const { loadCurrentUser, clearSession } = useAuthStore.getState();

    if (bypassEnabled) {
      setUser(mockDevUser);
      setUserId(mockDevUser.id);
      setHydrated(true);
      void loadCurrentUser();
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser) {
        setUser(null);
        setUserId(null);
        clearSession();
        setHydrated(true);
        return;
      }
      try {
        const profile = await syncUser(fbUser);
        setUser(profile);
        setUserId(profile.id);
        // NOTE: LOGIN is tracked in loginWith() (a genuine sign-in action), not
        // here — this callback also fires on every reload / token refresh for an
        // already-authenticated user, which would massively inflate LOGIN counts.
        // Hydrate RBAC store after user row is synced. Fire-and-forget: the
        // store marks itself hydrated when done; the spinner in ProtectedRoute
        // waits for both Firebase auth AND RBAC hydration.
        void loadCurrentUser();
      } catch (err) {
        console.error('User sync failed:', describeAuthError(err));
        // Network errors (backend restarting, offline) are transient — keep
        // the Firebase session alive so the user doesn't have to re-auth.
        // Only sign out for auth failures (e.g. token rejected by the server).
        const isNetworkError = err instanceof TypeError && err.message === 'Failed to fetch';
        if (!isNetworkError) {
          await firebaseLogout().catch(() => undefined);
          clearSession();
          setUser(null);
          setUserId(null);
        }
      } finally {
        setHydrated(true);
      }
    });
    return unsubscribe;
  }, [bypassEnabled, mockDevUser]);

  // Each provider login just opens the popup; the listener above performs the
  // backend sync and populates `user`.
  const loginWith = useCallback(
    async (id: ProviderId): Promise<AuthResult> => {
      if (bypassEnabled) {
        setUser({ ...mockDevUser, provider: id, updatedAt: new Date().toISOString() });
        setUserId(mockDevUser.id);
        trackEvent('LOGIN', 'auth', id);
        return { ok: true };
      }
      try {
        await signInWithProvider(id);
        trackEvent('LOGIN', 'auth', id);
        
        // If the user was already signed into Firebase but a previous network error
        // left the React state null, onAuthStateChanged may not fire again because
        // the Firebase user hasn't changed. We force a sync here to ensure the 
        // React state recovers.
        if (auth.currentUser) {
          try {
            const profile = await syncUser(auth.currentUser);
            setUser(profile);
            setUserId(profile.id);
            void useAuthStore.getState().loadCurrentUser();
          } catch (syncErr) {
            console.error('Forced user sync failed:', describeAuthError(syncErr));
            return { ok: false, error: 'Network error: could not reach the server. Please try again.' };
          }
        }
        return { ok: true };
      } catch (err) {
        return { ok: false, error: describeAuthError(err) };
      }
    },
    [bypassEnabled, mockDevUser],
  );

  const logout = useCallback(async () => {
    trackEvent('LOGOUT', 'auth');
    setUserId(null);
    useAuthStore.getState().clearSession();
    if (bypassEnabled) {
      setUser(null);
      return;
    }
    await firebaseLogout();
    setUser(null);
  }, [bypassEnabled]);

  const refreshProfile = useCallback(async () => {
    if (bypassEnabled) return;
    if (!auth.currentUser) return;
    try {
      const [profile] = await Promise.all([
        fetchProfile(),
        useAuthStore.getState().loadCurrentUser(),
      ]);
      setUser(profile);
    } catch {
      // ignore — better to keep the previous in-memory profile than evict
      // the user on a transient network error
    }
  }, [bypassEnabled]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      hydrated,
      loginWithGoogle: () => loginWith('google'),
      loginWithFacebook: () => loginWith('facebook'),
      loginWithMicrosoft: () => loginWith('microsoft'),
      logout,
      setUser,
      refreshProfile,
    }),
    [user, hydrated, loginWith, logout, refreshProfile],
  );

  // Terms is a legal gate; role selection is a product-classification gate.
  // Terms takes priority — a user who needs to accept the current version
  // shouldn't be asked to pick a role first, and stacking both modals at
  // once would be confusing. Role selection appears once Terms is clear.
  const showTermsModal = hydrated && rbacHydrated && user !== null && termsAcceptanceRequired;
  const showRoleModal =
    hydrated && rbacHydrated && user !== null && roleSelectionRequired && !termsAcceptanceRequired;

  return (
    <AuthContext.Provider value={value}>
      {children}
      <TermsAgreementModal open={showTermsModal} />
      <RoleSelectionModal open={showRoleModal} />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
